import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { getFeaturedPromoCardProperties } from '@/features/shared/analytics/promoCard'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { type HeroCardData, type HeroCardCarouselData } from '@/features/cms/components/CMS/types'
import { HeroCard } from '@/features/cms/components/CMS/components/Shelves/HeroCard'
import { getPromotionFromData } from '@/features/cms/components/CMS/utils/promotion'
import { CMSShelfLayoutContainerConditional } from '@/features/cms/components/CMS/components/Shelves/CMSShelfLayoutContainer'
import { Shelf } from '@/features/shop/components/Shelf'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { HeroCardCarouselV2 } from '@/features/cms/components/CMS/components/Shelves/HeroCardCarouselV2'
import { useQueryShoppingStores } from '@/features/shop/services/ShoppingStore/queries'
import { BotOnlyHeroCard } from '@/features/cms/components/CMS/components/Shelves/BotOnlyHeroCard'

type Props = HeroCardCarouselData & {
  index: number
}

/**
 * This component corresponds to the `hero_card_carousel` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/hero_card_carousel
 * - **Production** - https://cms.shipt.com/content-type/pool/hero_card_carousel
 */
export const HeroCardCarousel = ({ id, data, index, content_type_id, ...restProps }: Props) => {
  const cmsWrapperProps = getCMSWrapperProps({ content_type_id, id })
  const { trackCMSPromoCardViewed } = useCMSTrackingEvents()
  const { data: shoppingStores } = useQueryShoppingStores()

  const handleInView = (itemIndex: number, heroCardData: HeroCardData) => {
    const promotion = getPromotionFromData(heroCardData)
    trackCMSPromoCardViewed(
      getFeaturedPromoCardProperties({
        promotion,
        displayFormat: DISPLAY_FORMAT.HORIZONTAL,
        shelfIndex: index,
        listIndex: itemIndex,
        subLocation: cmsWrapperProps.id,
        nextLocation: heroCardData.data.link.url,
      }),
    )
  }

  if (!data.hero_cards.length) return null
  if (data.version === 'refresh') {
    return (
      <HeroCardCarouselV2
        id={id}
        data={data}
        index={index}
        content_type_id={content_type_id}
        {...restProps}
      />
    )
  }
  return (
    <CMSShelfLayoutContainerConditional {...cmsWrapperProps}>
      <section>
        <Shelf hideTitle title={content_type_id} shelfIndex={index} minHeight={190}>
          {data.hero_cards.map((heroCardData, itemIndex) => {
            return (
              <ScrollIntoView
                onInView={() => handleInView(itemIndex, heroCardData)}
                // Without a promo id, we can't guarantee that heading will be unique
                // or that any other properties will be present to check against
                // oxlint-disable-next-line react/no-array-index-key
                key={`${heroCardData.data.heading}-${itemIndex}`}
              >
                <HeroCard
                  heroCardData={heroCardData}
                  index={itemIndex}
                  shelfIndex={index}
                  shelfName={cmsWrapperProps.id}
                />
                <BotOnlyHeroCard
                  heroCardData={heroCardData}
                  deliveryStores={shoppingStores?.delivery_stores}
                />
              </ScrollIntoView>
            )
          })}
        </Shelf>
      </section>
    </CMSShelfLayoutContainerConditional>
  )
}
